import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Spin, message, Select } from 'antd';
import { SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import ExportOptions from '../common/ExportOptions';
import ReportService from '../../services/reportBuilder.service';

const { Option } = Select;

const TableWidget = ({
  widget,
  onEdit,
  onRefresh,
  isEditing = false,
  style = {}
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(widget?.config?.columns || []);

  useEffect(() => {
    if (widget?.config?.dataSource) {
      loadWidgetData();
    }
  }, [widget]);

  const loadWidgetData = async () => {
    if (!widget?.config?.dataSource) return;

    setLoading(true);
    try {
      const result = await ReportService.executeSavedSearch(
        widget.config.entityType || 'items',
        widget.config.dataSource,
        widget.config.limit || 100,
        0
      );

      if (result && result.data) {
        setData(result.data);
        // Auto-select all columns if none specified
        if (!selectedColumns.length && result.data.length > 0) {
          setSelectedColumns(Object.keys(result.data[0]));
        }
      }
    } catch (error) {
      console.error('Error loading widget data:', error);
      message.error('Failed to load widget data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadWidgetData();
    onRefresh && onRefresh(widget.id);
  };

  const handleColumnChange = (columns) => {
    setSelectedColumns(columns);
    if (onEdit) {
      onEdit(widget.id, {
        ...widget,
        config: {
          ...widget.config,
          columns
        }
      });
    }
  };

  // Generate table columns from selected columns
  const tableColumns = selectedColumns.map(col => ({
    title: col.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
    dataIndex: col,
    key: col,
    ellipsis: true,
    width: 150
  }));

  // Prepare export criteria
  const exportCriteria = widget?.config?.originalCriteria || {};

  return (
    <Card
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>{widget?.name || 'Table Widget'}</span>
          <Space>
            <ExportOptions
              entity={widget?.config?.entityType || 'items'}
              criteria={exportCriteria}
              data={data}
              filename={widget?.name || 'table-widget'}
            />
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              size="small"
            />
            {isEditing && (
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={() => onEdit && onEdit(widget.id)}
                size="small"
              />
            )}
          </Space>
        </Space>
      }
      style={{
        height: widget?.config?.size?.height || 400,
        ...style
      }}
      bodyStyle={{ height: 'calc(100% - 60px)', padding: '16px' }}
    >
      {isEditing ? (
        <Space direction="vertical" style={{ width: '100%', height: '100%' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Select Columns to Display:
            </label>
            <Select
              mode="multiple"
              value={selectedColumns}
              onChange={handleColumnChange}
              style={{ width: '100%' }}
              placeholder="Choose columns"
              maxTagCount={3}
            >
              {data.length > 0 && Object.keys(data[0]).map(field => (
                <Option key={field} value={field}>
                  {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ flex: 1, overflow: 'auto' }}>
            <Table
              dataSource={data.slice(0, 10)} // Show preview in edit mode
              columns={tableColumns}
              size="small"
              pagination={false}
              scroll={{ y: 200 }}
            />
          </div>
        </Space>
      ) : (
        <Spin spinning={loading}>
          <Table
            dataSource={data}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 250 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
            }}
          />
        </Spin>
      )}
    </Card>
  );
};

export default TableWidget;